import axios from "axios";
import { api, apiNew } from "./Host";

const apiClient = axios.create();
const jwtLikePattern = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/;
const isoHeaderPattern = /^[\x20-\x7E]+$/;

const normalizeToken = (token) => {
  if (typeof token !== "string") {
    return null;
  }

  const trimmedToken = token.trim();
  if (!trimmedToken) {
    return null;
  }

  if (!isoHeaderPattern.test(trimmedToken)) {
    return null;
  }

  if (!jwtLikePattern.test(trimmedToken)) {
    return null;
  }

  return trimmedToken;
};

apiClient.interceptors.request.use((config) => {
  const token = normalizeToken(localStorage.getItem("auth_token"));
  config.headers = config.headers ?? {};
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem("auth_token");
  }
  return config;
});

let sessionReplacementNotified = false;

const notifySessionReplacement = () => {
  if (sessionReplacementNotified) {
    return;
  }

  sessionReplacementNotified = true;
  window.dispatchEvent(new CustomEvent("e-test-session-replaced"));
};

apiClient.interceptors.response.use(
  response => response,
  error => {
    const response = error?.response;
    const authError = response?.headers?.["x-e-test-auth-error"] || response?.data?.code;
    if (response?.status === 401 && authError === "SESSION_REPLACED") {
      notifySessionReplacement();
    } else if (response?.status === 401 && authError === "SESSION_NOT_ACTIVE") {
      clearAuth();
      window.location.replace("/");
    }
    return Promise.reject(error);
  }
);

export const clearAuth = () => {
  localStorage.removeItem("auth_token");
  localStorage.removeItem("auth_user_id");
};

export const acknowledgeSessionReplacement = () => {
  sessionReplacementNotified = false;
};

export const setAuthToken = (token, userId) => {
  const normalizedToken = normalizeToken(token);
  if (normalizedToken) {
    localStorage.setItem("auth_token", normalizedToken);
  } else {
    localStorage.removeItem("auth_token");
  }
  if (userId !== undefined && userId !== null) {
    localStorage.setItem("auth_user_id", String(userId));
  }
};

export const getRegions = async () => {
  return apiClient.get(`${apiNew}/api/Regions/GetRegions`);
};

export const getSchools = async (id) => {
  return apiClient.get(`${apiNew}/api/Schools/GetSchoolsByRegionId?regionId=${id}`);
};

export const getSchoolId = async (id) => {
  return apiClient.get(`${apiNew}/api/Schools/GetSchoolById?schoolId=${id}`);
};

export const getToken = async (id) => {
  // Password login now receives an E-TEST JWT from the local backend.
  // This fallback is retained only for old clients during rollout.
  return apiClient.get(`${api}/api/TestStart/getToken?userId=${id}`);
};

export const getLogin = async (data) => {
  return apiClient.post(`${apiNew}/api/Users/LoginETest`, data);
};

export const checkFace = async (data) => {
  return apiClient.post(`${apiNew}/api/Users/CheckUserFace/`, data);
};

export const checkUsernamePhone = async (data) => {
  return apiClient.get(`${apiNew}/api/Users/CheckUserByUsernameOrPhone?${data.phone !== null ? `phone=${data.phone}&` : ""}${data.username !== null ? `username=${data.username}` : ""}`);
};

export const getImages = async (id) => {
  // Do not mix the production image endpoint with a local API session.  A
  // local JWT is unknown to e-test.uz and its 401 interceptor would redirect
  // the completed-result page back to login.
  return apiClient.get(`${apiNew}/api/Users/GetUserImagesByQuestionsHistoryId?userQuestionsHistoryId=${id}`);
};

export const getUser = async (id) => {
  return apiClient.get(`${apiNew}/api/Users/getUserById?id=${id}`);
};

export const getTest = async (id) => {
  return apiClient.get(`${apiNew}/api/Users/GetUserQuestionsByUserId?user_id=${id}`);
};

export const getTestLast = async (id) => {
  return apiClient.get(`${apiNew}/api/Users/GetLastUserTestSession?user_id=${id}`);
};

export const getExternalFinalTestResult = async (ticket) => {
  return apiClient.get(`${apiNew}/api/ExternalFinalTestResults`, {
    headers: { "X-ETest-Result-Ticket": ticket }
  });
};

export const setAnswer = async (uq_id, q_id, a_id, timer) => {
  return apiClient.post(`${apiNew}/api/Users/SaveUserTestSession?userQuestionsHistoryId=${uq_id}&questionId=${q_id}&answerId=${a_id}&timer=${timer}`);
};

export const setImage = async (t_id, data) => {
  return apiClient.post(`${apiNew}/api/Users/SaveUserImage?userQuestionsHistoryId=${t_id}`, data);
};
